import uPlot from 'uplot';
import { useEffect, useRef } from 'react';
import { useResolvedTheme } from '@/hooks/useTheme';
import { playbackClock } from '@/lib/playbackClock';
import type { TelemetrySeries } from '@/lib/telemetry-series';
import { useTelemetryStore } from '@/stores/telemetryStore';

export interface ChannelSpec {
  key: keyof TelemetrySeries;
  label: string;
  color: string;
}

interface TelemetryChartProps {
  title: string;
  channels: ChannelSpec[];
}

/**
 * One `<TelemetryChart>` per panel in the 10-chart grid. Data is set exactly
 * once, when a run becomes ready — the whole flight is already known (see
 * `sim.worker.ts`), so there is no "growing line" to animate. What *does*
 * animate is the playhead: a `draw` hook repaints a vertical marker at
 * `playbackClock.time` on every animation frame via a cheap `redraw(false,
 * false)` (reuses cached paths, no data reprocessing), so scrubbing and
 * playback feel live without React re-rendering anything.
 */
export function TelemetryChart({ title, channels }: TelemetryChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const status = useTelemetryStore((state) => state.status);
  const resolvedTheme = useResolvedTheme();

  useEffect(() => {
    const container = containerRef.current;
    if (status !== 'ready' || !container) return undefined;

    const series = useTelemetryStore.getState().series;
    if (!series) return undefined;

    const axisColor = resolvedTheme === 'dark' ? '#8b97a8' : '#5a6472';
    const gridColor = resolvedTheme === 'dark' ? 'rgba(139, 151, 168, 0.16)' : 'rgba(90, 100, 114, 0.16)';
    const playheadColor = resolvedTheme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)';

    const plot = new uPlot(
      {
        width: container.clientWidth,
        height: container.clientHeight,
        title,
        series: [{ label: 'T+ (s)' }, ...channels.map((channel) => ({ label: channel.label, stroke: channel.color, width: 1.5 }))],
        axes: [
          { stroke: axisColor, grid: { stroke: gridColor } },
          { stroke: axisColor, grid: { stroke: gridColor } },
        ],
        scales: { x: { time: false } },
        legend: { show: channels.length > 1 },
        cursor: { drag: { x: false, y: false } },
        hooks: {
          draw: [
            (u) => {
              if (playbackClock.duration <= 0) return;
              const x = u.valToPos(playbackClock.time, 'x', true);
              const ctx = u.ctx;
              ctx.save();
              ctx.strokeStyle = playheadColor;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(x, u.bbox.top);
              ctx.lineTo(x, u.bbox.top + u.bbox.height);
              ctx.stroke();
              ctx.restore();
            },
          ],
        },
      },
      [series.t, ...channels.map((channel) => series[channel.key])],
      container,
    );

    // Repaint only when the playhead has actually moved. The marker is the
    // only thing that animates, so redrawing unconditionally burnt a full
    // uPlot repaint per chart per frame — ten charts at 60 Hz — for the
    // entire time the app was open, including while paused and before any
    // run had been loaded.
    let lastDrawnTime = Number.NaN;
    let raf = requestAnimationFrame(function tick() {
      if (playbackClock.time !== lastDrawnTime) {
        lastDrawnTime = playbackClock.time;
        plot.redraw(false, false);
      }
      raf = requestAnimationFrame(tick);
    });

    const resizeObserver = new ResizeObserver(() => {
      plot.setSize({ width: container.clientWidth, height: container.clientHeight });
    });
    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      plot.destroy();
    };
  }, [status, resolvedTheme, channels, title]);

  if (status !== 'ready') {
    return (
      <div className="flex h-full items-center justify-center text-xs text-faint">
        {status === 'running' ? 'Simulating…' : status === 'error' ? 'Simulation failed' : 'No simulation run yet'}
      </div>
    );
  }

  return <div ref={containerRef} className="h-full w-full" />;
}
